import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Search } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import StickyCallButton from '@/components/StickyCallButton';
import PageSEO from '@/components/PageSEO';
import { Input } from '@/components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CoverageHub = () => {
  const [cities, setCities] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchCities = async () => {
      try {
        const response = await axios.get(`${API}/cities`);
        setCities(response.data);
      } catch (error) {
        console.error('Fehler beim Laden der Städte:', error);
      }
    };
    fetchCities();
  }, []);

  const filteredCities = cities.filter(city =>
    city.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedCities = [...filteredCities].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <PageSEO
        title="Einsatzgebiete | Handwerkernotdienst NRW im Ruhrgebiet"
        description="24/7 Handwerkernotdienst in Dortmund, Bochum, Essen und Umgebung. Wir sind in über 30 Städten im Ruhrgebiet für Sie da. ☎ 0231 39083687"
        canonicalUrl="https://handwerkernotdienst.nrw/einsatzgebiete"
        breadcrumbs={[
          { name: 'Start', url: 'https://handwerkernotdienst.nrw/' },
          { name: 'Einsatzgebiete', url: 'https://handwerkernotdienst.nrw/einsatzgebiete' }
        ]}
      />
      
      <Header />
      <StickyCallButton />

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#e8f4f8] to-white py-16" data-testid="coverage-hero">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4" data-testid="coverage-title">
            Unsere Einsatzgebiete
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Wir sind im Raum Dortmund und Umgebung für Sie im Einsatz. Wählen Sie Ihre Stadt, 
            um mehr über unseren Service in Ihrer Region zu erfahren.
          </p>
        </div>
      </section>

      {/* Search */}
      <section className="py-8 border-b border-gray-200" data-testid="coverage-search">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative max-w-md mx-auto">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="Stadt suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="city-search-input"
            />
          </div>
        </div>
      </section>

      {/* Cities Grid */}
      <section className="py-16" data-testid="cities-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {sortedCities.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {sortedCities.map((city, index) => (
                <Link
                  key={index}
                  to={`/einsatzgebiete/${city.slug}`}
                  className="location-card bg-gray-50 p-6 rounded-xl"
                  data-testid={`coverage-city-card-${index}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <MapPin className="w-6 h-6 text-[#0066cc] flex-shrink-0" />
                      <h3 className="text-xl font-bold text-gray-900">{city.name}</h3>
                    </div>
                    <span className="text-sm text-gray-500">{city.postal_codes?.length || 0} PLZ</span>
                  </div>
                  <p className="text-gray-600 mb-3">{city.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">
                      {city.districts?.length || 0} Stadtteile
                    </span>
                    <span className="text-sm text-[#0066cc] font-semibold">Mehr erfahren →</span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">Keine Städte gefunden. Versuchen Sie eine andere Suche.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gray-50" data-testid="coverage-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Ist Ihre Stadt nicht dabei?
          </h2>
          <p className="text-lg text-gray-600 mb-6">
            Rufen Sie uns trotzdem an! Wir sind flexibel und prüfen gerne, 
            ob wir Ihnen in Ihrem Notfall helfen können.
          </p>
          <a
            href="tel:+4923139083687"
            className="inline-block bg-[#0066cc] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#0052a3] transition-colors"
            data-testid="coverage-cta-button"
          >
            Jetzt anrufen: 0231 39083687
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CoverageHub;